import React, { memo } from 'react';
import { sanitizeRichText } from '../utils/sanitize';

// Helper function to strip HTML tags and decode HTML entities
const stripHtmlTags = (html) => {
  if (!html || typeof html !== 'string') return '';
  
  return html
    // Remove all HTML tags
    .replace(/<[^>]*>/g, '')
    // Decode HTML entities
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    // Clean up extra whitespace
    .replace(/\s+/g, ' ')
    .trim();
};

const ServiceDetailContent = memo(({ subservice, details, language, onOpenChat }) => {
  // Content - prioritize details, then subservice, then content fields
  let content = '';
  if (details) {
    if (language === 'ar') {
      content = details.content_ar || details.content_en || details.description_ar || details.description_en || '';
    } else {
      content = details.content_en || details.content_ar || details.description_en || details.description_ar || '';
    }
  }
  // If no details, try from subservice
  if (!content) {
    if (language === 'ar') {
      content = subservice?.content_ar || subservice?.content_en || subservice?.description_ar || subservice?.description_en || '';
    } else {
      content = subservice?.content_en || subservice?.content_ar || subservice?.description_en || subservice?.description_ar || '';
    }
  }
  
  const features = details?.features || [];
  
  // Process steps - prioritize details, then subservice
  let processSteps = [];
  if (details) {
    if (language === 'ar') {
      processSteps = details.process_steps_arb || details.process_steps_ar || details.process_steps || [];
    } else {
      processSteps = details.process_steps_eng || details.process_steps_en || details.process_steps || [];
    }
  }
  // If no details, try from subservice directly
  if (processSteps.length === 0 && subservice) {
    if (language === 'ar') {
      const stepsAr = subservice.process_steps_arb || subservice.process_steps_ar;
      if (stepsAr) {
        try {
          if (typeof stepsAr === 'string') {
            if (stepsAr.trim().startsWith('[') || stepsAr.trim().startsWith('{')) {
              processSteps = JSON.parse(stepsAr);
            } else {
              processSteps = stepsAr.split(/\n|•|·|-/).map(p => p.trim()).filter(p => p.length > 0);
            }
          } else if (Array.isArray(stepsAr)) {
            processSteps = stepsAr;
          }
        } catch (e) {
          processSteps = [];
        }
      }
    } else {
      const stepsEn = subservice.process_steps_eng || subservice.process_steps_en;
      if (stepsEn) {
        try {
          if (typeof stepsEn === 'string') {
            if (stepsEn.trim().startsWith('[') || stepsEn.trim().startsWith('{')) {
              processSteps = JSON.parse(stepsEn);
            } else {
              processSteps = stepsEn.split(/\n|•|·|-/).map(p => p.trim()).filter(p => p.length > 0);
            }
          } else if (Array.isArray(stepsEn)) {
            processSteps = stepsEn;
          }
        } catch (e) {
          processSteps = [];
        }
      }
    }
  }
  
  // Documents required - prioritize details, then subservice
  let documentsRequired = [];
  if (details) {
    if (language === 'ar') {
      documentsRequired = details.documents_required_arb || details.documents_required_ar || details.documents_required || [];
    } else {
      documentsRequired = details.documents_required_eng || details.documents_required_en || details.documents_required || [];
    }
  }
  // If no details, try from subservice directly
  if (documentsRequired.length === 0 && subservice) {
    if (language === 'ar') {
      const docsAr = subservice.documents_required_arb || subservice.documents_required_ar;
      if (docsAr) {
        try {
          if (typeof docsAr === 'string') {
            if (docsAr.trim().startsWith('[') || docsAr.trim().startsWith('{')) {
              documentsRequired = JSON.parse(docsAr);
            } else {
              documentsRequired = docsAr.split(/\n|•|·|,|;/).map(d => d.trim()).filter(d => d.length > 0);
            }
          } else if (Array.isArray(docsAr)) {
            documentsRequired = docsAr;
          }
        } catch (e) {
          documentsRequired = [];
        }
      }
    } else {
      const docsEn = subservice.documents_required_eng || subservice.documents_required_en;
      if (docsEn) {
        try {
          if (typeof docsEn === 'string') {
            if (docsEn.trim().startsWith('[') || docsEn.trim().startsWith('{')) {
              documentsRequired = JSON.parse(docsEn);
            } else {
              documentsRequired = docsEn.split(/\n|•|·|,|;/).map(d => d.trim()).filter(d => d.length > 0);
            }
          } else if (Array.isArray(docsEn)) {
            documentsRequired = docsEn;
          }
        } catch (e) {
          documentsRequired = [];
        }
      }
    }
  }
  
  // Duration - from details or subservice
  const duration = details?.duration || subservice?.duration || '';

  // Parse content to extract text if it's HTML
  const getTextContent = (html) => {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Main Content - Enhanced Design */}
      {content && (
        <section style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          <div 
            style={{
              fontSize: '16px',
              lineHeight: '1.7',
              color: '#374151',
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }}
            dangerouslySetInnerHTML={{ __html: sanitizeRichText(content) }}
          />
        </section>
      )}

      {/* Features - Enhanced Grid Design */}
      {features.length > 0 && (
        <section style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          <h2 style={{
            fontSize: '22px',
            fontWeight: '700',
            color: '#1a1a2e',
            marginBottom: '16px',
            paddingBottom: '12px',
            borderBottom: '2px solid #1a1a2e'
          }}>
            {language === 'ar' ? 'المميزات الرئيسية' : 'Key Features'}
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '16px'
          }}>
            {features.map((feature, index) => (
              <div
                key={index}
                style={{
                  padding: '16px',
                  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                  borderRadius: '10px',
                  border: '1px solid #e2e8f0',
                  transition: 'all 0.3s',
                  cursor: 'default'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12)';
                  e.currentTarget.style.borderColor = '#1a1a2e';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <svg
                      width="18"
                      height="18"
                      fill="none"
                      stroke="white"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <p style={{
                    fontSize: '16px',
                    lineHeight: '1.6',
                    color: '#1e293b',
                    margin: 0,
                    fontWeight: '500'
                  }}>
                    {typeof feature === 'string' ? feature : feature.title || feature.name || JSON.stringify(feature)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Process Steps - Enhanced Timeline Design */}
      {processSteps.length > 0 && (
        <section style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          <h2 style={{
            fontSize: '22px',
            fontWeight: '700',
            color: '#1a1a2e',
            marginBottom: '20px',
            paddingBottom: '12px',
            borderBottom: '2px solid #1a1a2e'
          }}>
            {language === 'ar' ? 'خطوات العملية' : 'Process Steps'}
          </h2>
          <div style={{ position: 'relative' }}>
            {/* Timeline Line */}
            <div style={{
              position: 'absolute',
              left: '20px',
              top: '0',
              bottom: '0',
              width: '2px',
              background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
              borderRadius: '2px'
            }}></div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', position: 'relative' }}>
              {processSteps.map((step, index) => {
                const stepData = typeof step === 'string' 
                  ? { description: step }
                  : step;
                const stepTitle = stepData.title || stepData.name || '';
                const stepDesc = stepData.description || stepData.desc || stepData || '';

                return (
                  <div key={index} style={{ display: 'flex', gap: '16px', position: 'relative' }}>
                    {/* Step Number Circle */}
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                      fontWeight: '700',
                      flexShrink: 0,
                      boxShadow: '0 4px 12px rgba(26, 26, 46, 0.3)',
                      zIndex: 1
                    }}>
                      {index + 1}
                    </div>

                    {/* Step Content */}
                    <div style={{ flex: 1, paddingTop: '2px' }}>
                      {stepTitle && (
                        <h3 style={{
                          fontSize: '18px',
                          fontWeight: '600',
                          color: '#1a1a2e',
                          marginBottom: '6px'
                        }}>
                          {stepTitle}
                        </h3>
                      )}
                      <p style={{
                        fontSize: '15px',
                        lineHeight: '1.6',
                        color: '#64748b',
                        margin: 0
                      }}>
                        {stepDesc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Documents Required - Enhanced Card Design */}
      {documentsRequired.length > 0 && (
        <section style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          <h2 style={{
            fontSize: '22px',
            fontWeight: '700',
            color: '#1a1a2e',
            marginBottom: '16px',
            paddingBottom: '12px',
            borderBottom: '2px solid #1a1a2e'
          }}>
            {language === 'ar' ? 'المستندات المطلوبة' : 'Required Documents'}
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '16px'
          }}>
            {documentsRequired.map((doc, index) => (
              <div
                key={index}
                style={{
                  padding: '12px 16px',
                  background: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f1f5f9';
                  e.currentTarget.style.borderColor = '#1a1a2e';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f8fafc';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                }}
              >
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <svg
                    width="20"
                    height="20"
                    fill="none"
                    stroke="white"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <span style={{
                  fontSize: '15px',
                  color: '#1e293b',
                  fontWeight: '500'
                }}>
                  {typeof doc === 'string' ? doc : doc.name || doc.title || doc.description || JSON.stringify(doc)}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Duration Card */}
      {duration && (
        <section style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          borderRadius: '12px',
          padding: '24px',
          color: 'white',
          boxShadow: '0 8px 24px rgba(26, 26, 46, 0.25)',
          width: '100%',
          maxWidth: '400px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          marginTop: '32px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            marginBottom: '16px'
          }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              flexShrink: 0,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
            }}>
              <svg 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                style={{ color: 'white' }}
              >
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              margin: 0,
              lineHeight: '1.3'
            }}>
              {language === 'ar' ? 'المدة المتوقعة' : 'Estimated Duration'}
            </h3>
          </div>
          <div style={{
            paddingLeft: '70px'
          }}>
            <p style={{
              fontSize: '28px',
              fontWeight: '700',
              margin: 0,
              lineHeight: '1.2',
              opacity: 0.95,
              letterSpacing: '0.5px'
            }}>
              {duration}
            </p>
          </div>
        </section>
      )}

      {/* Fallback: Show subservice description if no details available */}
      {!content && !features.length && !processSteps.length && !documentsRequired.length && !duration && subservice?.description && (
        <section style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          <h2 style={{
            fontSize: '22px',
            fontWeight: '700',
            color: '#1a1a2e',
            marginBottom: '16px',
            paddingBottom: '12px',
            borderBottom: '2px solid #1a1a2e'
          }}>
            {language === 'ar' ? 'وصف الخدمة' : 'Service Description'}
          </h2>
          <div 
            style={{
              fontSize: '16px',
              lineHeight: '1.7',
              color: '#374151',
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }}
            dangerouslySetInnerHTML={{ __html: sanitizeRichText(subservice.description) }}
          />
        </section>
      )}

      {/* Responsive Styles for Duration & CTA */}
      <style>{`
        @media (max-width: 768px) {
          div[style*="display: flex"][style*="gap: 20px"] {
            flex-direction: column !important;
          }
          div[style*="display: flex"][style*="gap: 20px"] > section {
            min-width: 100% !important;
          }
        }
      `}</style>
    </div>
  );
});

ServiceDetailContent.displayName = 'ServiceDetailContent';

export default ServiceDetailContent;
